import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import EditIcon from '@mui/icons-material/Edit';
import PreviewIcon from '@mui/icons-material/Preview';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { StageProcessor } from '../components/StageProcessor';
import { generateSectionB } from '../api/stageFunctions';
import { useStageCache } from '../hooks/useStageCache';
import { STAGE_NUMBERS } from '../utils/cacheUtils';
import { getButtonText } from '../utils/cacheUIHelpers';

interface SectionBGenerationProps {
  productsWithVendors: any[];
  onComplete?: (finalDocument: string) => void; // Make optional since we won't use it
  flowId?: string | null;
}

const SectionBGeneration: React.FC<SectionBGenerationProps> = ({
  productsWithVendors,
  onComplete,
  flowId,
}) => {
  const [sectionBContent, setSectionBContent] = useState<string>('');
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [triggerGenerate, setTriggerGenerate] = useState(0);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [useCache, setUseCache] = useState(true);
  
  // One-time cache validation aligned with backend; UI shows status only
  // Backend saves Stage 7 with "products" field, not "products_with_vendors"
  const { cachedData, isCacheValid, isCheckingCache, validateCacheWithInput } = useStageCache({
    flowId,
    stageNumber: STAGE_NUMBERS.SECTION_B,
    inputData: { 
      products: productsWithVendors,
      quantities: null,
      delivery_date: "90 days ARO",
      contract_type: "FFP"
    },
    enabled: true,
  });

  // Validate cache once based on vendor-search output
  useEffect(() => {
    if (!hasInitialized && flowId) {
      setHasInitialized(true);
      validateCacheWithInput();
    }
  }, [hasInitialized, flowId, validateCacheWithInput]);

  // Default checkbox to match cache availability
  useEffect(() => {
    if (!isCheckingCache) {
      setUseCache(isCacheValid);
    }
  }, [isCheckingCache, isCacheValid]);

  // Auto-generate on mount after cache validation completes
  useEffect(() => {
    if (!isCheckingCache && sectionBContent === '') {
      handleGenerateSectionB();
    }
  }, [isCheckingCache, sectionBContent]);
  
  // Load cached data when available AND user wants to use cache
  useEffect(() => {
    if (isCacheValid && cachedData && !sectionBContent && useCache) {
      handleSectionBGenerated(cachedData);
    }
  }, [isCacheValid, cachedData, sectionBContent, useCache]);

  const handleGenerateSectionB = () => {
    // Clear existing content if not using cache to force regeneration
    if (!useCache && sectionBContent) {
      setSectionBContent('');
    }
    setTriggerGenerate(prev => prev + 1);
  };

  const handleSectionBGenerated = (data: any) => {
    // With UI-ready format, content is always in sectionBContent field as a string
    const content = data.sectionBContent || data.section_b_content || data;
    
    if (typeof content !== 'string') {
      console.error('SectionBGeneration: Expected string content but got:', data);
      setSectionBContent('');
      return;
    }
    
    // UI-ready format guarantees string content - no transformation needed
    setSectionBContent(content);
  };

  const handleModeChange = (
    event: React.MouseEvent<HTMLElement>,
    newMode: string | null,
  ) => {
    if (newMode !== null) {
      setIsEditMode(newMode === 'edit');
    }
  };

  const handleExportMarkdown = () => {
    const blob = new Blob([sectionBContent], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `section-b-${Date.now()}.md`;
    link.click();
  };

  const handleExportPDF = async () => {
    if (!sectionBContent.trim()) {
      alert('Please generate Section B content first');
      return;
    }

    try {
      // Call backend API to generate PDF using Pandoc
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${apiUrl}/api/section-b/generate-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          markdown_content: sectionBContent
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }

      // Get the PDF blob and download it
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `section-b-${Date.now()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`Error generating PDF: ${errorMessage}`);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          <DescriptionIcon sx={{ mr: 1, verticalAlign: 'bottom' }} />
          Section B Generation
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Generate and edit Section B - Supplies or Services and Prices/Costs. 
          This document defines the contract line items, quantities, and pricing structure.
        </Typography>
      </Paper>

      {/* Generate Section B */}
      {/* Cache Control and Generate Button */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={useCache && isCacheValid}
                onChange={(e) => setUseCache(e.target.checked)}
                color="primary"
                disabled={isCheckingCache || !isCacheValid}
              />
            }
            label="Use cached data when available"
          />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<DescriptionIcon />}
            onClick={() => setTriggerGenerate(prev => prev + 1)}
            disabled={isCheckingCache}
          >
            {getButtonText('Generate Section B', useCache, cachedData && cachedData.section_b_content)}
          </Button>
        </Box>
      </Paper>
      
      <StageProcessor
        title="Generating Section B"
        description="Creating procurement document from vendor and product data..."
        processFunction={() => generateSectionB(productsWithVendors, flowId, !useCache)}
        onSuccess={handleSectionBGenerated}
        trigger={triggerGenerate}
        autoProcess={false}
        showStatus={true}
      >
        {(data, status) => {
          if (!data || !sectionBContent) return null;

          return (
            <>
              {/* Mode Toggle */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Box display="flex" justifyContent="center">
                  <ToggleButtonGroup
                    value={isEditMode ? 'edit' : 'preview'}
                    exclusive
                    onChange={handleModeChange}
                    aria-label="document mode"
                  >
                    <ToggleButton value="preview" aria-label="preview mode">
                      <PreviewIcon sx={{ mr: 1 }} />
                      Preview
                    </ToggleButton>
                    <ToggleButton value="edit" aria-label="edit mode">
                      <EditIcon sx={{ mr: 1 }} />
                      Edit
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Paper>

              {/* Content Area */}
              <Paper sx={{ p: 3, mb: 2, minHeight: 600 }}>
                {isEditMode ? (
                  // Edit Mode
                  <TextField
                    multiline
                    fullWidth
                    variant="outlined"
                    value={sectionBContent}
                    onChange={(e) => setSectionBContent(e.target.value)}
                    minRows={25}
                    sx={{
                      '& .MuiInputBase-root': {
                        fontFamily: 'monospace',
                        fontSize: '14px',
                      }
                    }}
                    placeholder="Section B content in markdown format..."
                  />
                ) : (
                  // Preview Mode
                  <Box
                    id="section-b-preview"
                    sx={{
                      '& table': {
                        borderCollapse: 'collapse',
                        width: '100%',
                        mb: 2,
                      },
                      '& th, & td': {
                        border: '1px solid #ddd',
                        padding: '8px',
                        textAlign: 'left',
                      },
                      '& th': {
                        backgroundColor: '#f5f5f5',
                        fontWeight: 'bold',
                      },
                      '& h1': {
                        fontSize: '2rem',
                        fontWeight: 'bold',
                        mb: 2,
                        mt: 0,
                      },
                      '& h2': {
                        fontSize: '1.5rem',
                        fontWeight: 'bold',
                        mb: 1.5,
                        mt: 3,
                      },
                      '& h3': {
                        fontSize: '1.2rem',
                        fontWeight: 'bold',
                        mb: 1,
                        mt: 2,
                      },
                      '& p': {
                        mb: 1.5,
                        lineHeight: 1.6,
                      },
                      '& ul, & ol': {
                        mb: 1.5,
                        pl: 3,
                      },
                      '& li': {
                        mb: 0.5,
                      },
                      '& code': {
                        backgroundColor: '#f5f5f5',
                        padding: '2px 4px',
                        borderRadius: '3px',
                        fontFamily: 'monospace',
                      },
                      '& pre': {
                        backgroundColor: '#f5f5f5',
                        padding: '12px',
                        borderRadius: '4px',
                        overflow: 'auto',
                      },
                    }}
                  >
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {sectionBContent}
                    </ReactMarkdown>
                  </Box>
                )}
              </Paper>

              {/* Info Alert */}
              {isEditMode && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  You are in edit mode. Make any necessary changes to the Section B document. 
                  The content is in markdown format - tables, headers, and formatting will be 
                  rendered in preview mode.
                </Alert>
              )}

              {/* Action Buttons */}
              <Paper sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Section B document is ready. Export as needed for your procurement package.
                  </Typography>
                  <Box>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={handleExportMarkdown}
                      sx={{ mr: 2 }}
                    >
                      Export Markdown
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<PictureAsPdfIcon />}
                      onClick={handleExportPDF}
                      sx={{ 
                        backgroundColor: '#d32f2f',
                        '&:hover': { backgroundColor: '#b71c1c' }
                      }}
                    >
                      Export PDF
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </>
          );
        }}
      </StageProcessor>
    </Box>
  );
};

export default SectionBGeneration;
